// File Location: src/pages/dashboard/FinancialDashboard.jsx

import React, { useState, useEffect, useCallback } from "react";
import samhealthLogo from '../../assets/samhealth.png';
import { useNavigate, useLocation } from "react-router-dom";
import {
  BarChart3,
  FileText,
  User,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  TrendingUp,
  CreditCard,
  RefreshCw,
  Menu,
  X,
  Calculator,
  Receipt,
  Banknote,
  Bell,
  Activity,
  Target,
  TrendingDown,
  Package,
  Building,
  FolderOpen,
  ArrowUpDown,
  Eye,
  Calendar
} from "lucide-react";
import ClampedText from "../../components/common/ClampedText.jsx";
import NotificationBell from '../../components/common/NotificationBell';
import RoleSwitcher from '../../components/common/RoleSwitcher';
import UniversalSearch from '../../components/common/UniversalSearch';
import FinancialApplicationsList from "../../screens/financial/FinancialApplicationsList";
import FinancialHistory from "../../screens/financial/FinancialHistory";
import FinancialProfile from "../../screens/financial/FinancialProfile";
import DisbursalTracking from "../../screens/financial/DisbursalTracking";
import DispatchTracking from "../../screens/financial/DispatchTracking";
import DischargeSettlement from "../../screens/financial/DischargeSettlement";
import ENachProcessList from "../../screens/financial/ENachProcessList";
import PendingDataTab from "../../screens/financial/PendingDataTab";
import { useAuth } from "../../context/AuthContext";
import { getFinancialTeamStats } from "../../services/financialservice";
import RecoveryTracking from "./RecoveryTracking";
import { SignOutIconButton } from "../../component/common/SignOutButton";
import { getAllApplicationsAdmin } from "../../services/doctorService";

const FinancialDashboard = () => {
  const { userData: user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dashboardStats, setDashboardStats] = useState({
    pendingProcessing: 0,
    processedToday: 0,
    totalProcessed: 0,
    averageProcessingTime: 0,
    emergencyPending: 0,
    approvalRate: 0,
    approvedCount: 0,
    rejectedCount: 0,
    pendingInfoCount: 0,
    totalDisbursedAmount: 0,
    totalClaimAmount: 0,
    disbursalsPending: 0,
    recoveryPending: 0,
    averageDisbursalRatio: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [notifications, setNotifications] = useState([]);
  // Sync active section from query string (?section=...)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const section = params.get('section');
      if (section) {
        setActiveSection(section);
      }
    } catch (e) {
      // ignore
    }
  }, [location.search]);

  // All Applications (finance team) state
  const [allApplications, setAllApplications] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allSearch, setAllSearch] = useState("");
  const [allPage, setAllPage] = useState(1);
  const [allTotalPages, setAllTotalPages] = useState(1);
  const [allFinanceStage, setAllFinanceStage] = useState('all');
  const [allFinanceStageCounts, setAllFinanceStageCounts] = useState({});
  // Keep a raw cache for counts and client-side filtering
  const [allApplicationsRaw, setAllApplicationsRaw] = useState([]);

  const sidebarItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: BarChart3,
      count: null,
      description: "Overview & Analytics"
    },
 
    {
      id: "all-applications",
      label: "All Applications",
      icon: BarChart3,
      count: null,
      description: "Browse every application"
    },
    {
      id: "challenged",
      label: "Challenged (KYP)",
      icon: AlertTriangle,
      count: null,
      description: "KYP challenged and reassigned to Finance"
    },
    {
      id: "pending-data",
      label: "Pending Data",
      icon: Clock,
      count: null,
      description: "Applications with pending data requirements"
    },
    {
      id: "settlement",
      label: "Discharge Settlement",
      icon: Calculator,
      count: null,
      description: "Calculate final settlement amounts"
    },
    {
      id: "disbursal",
      label: "Disbursal Tracking",
      icon: CreditCard,
      count: dashboardStats.disbursalsPending,
      description: "Track disbursements to lenders"
    },
    {
      id: "enach",
      label: "eNach",
      icon: CreditCard,
      count: dashboardStats.enachPending,
      description: "Initiate & monitor eNach"
    },
    {
      id: "dispatches",
      label: "Dispatch Tracking",
      icon: Package,
      count: null,
      description: "Track dispatched documents"
    },
    {
      id: "recovery",
      label: "Recovery Tracking",
      icon: Banknote,
      count: dashboardStats.recoveryPending,
      description: "Monitor insurance recoveries"
    },
    {
      id: "history",
      label: "Financial History",
      icon: Receipt,
      count: null,
      description: "Your processing history"
    },
    {
      id: "documents",
      label: "Documents",
      icon: FolderOpen,
      count: null,
      description: "Access all documents"
    },
    {
      id: "profile",
      label: "Profile",
      icon: User,
      count: null,
      description: "Account settings & preferences"
    },
  ];

  // Auto-refresh every 30 seconds for real-time updates
  useEffect(() => {
    if (user?.uid && activeSection === 'dashboard') {
      const interval = setInterval(() => {
        loadDashboardData(true); // Silent refresh
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.uid, activeSection]);

  useEffect(() => {
    if (user?.uid) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = useCallback(async (silent = false) => {
    if (!user?.uid) return;

    if (!silent) setLoading(true);
    try {
      const result = await getFinancialTeamStats(user.uid);
      if (result.success) {
        setDashboardStats(result.stats);
        setLastUpdated(new Date());
        
        // Generate notifications based on stats
        const newNotifications = [];
        if (result.stats.emergencyPending > 0) {
          newNotifications.push({
            id: 'emergency',
            type: 'urgent',
            message: `${result.stats.emergencyPending} emergency cases require immediate attention`,
            action: () => setActiveSection('applications')
          });
        }
        if (result.stats.disbursalsPending > 5) {
          newNotifications.push({
            id: 'disbursals',
            type: 'warning',
            message: `${result.stats.disbursalsPending} disbursals are pending processing`,
            action: () => setActiveSection('disbursal')
          });
        }
        setNotifications(newNotifications);
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user?.uid]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  // Strict Finance domain guard: include all applications that could be in finance buckets
  const isFinanceDomainApplication = React.useCallback((application) => {
    // Include ALL applications - let the bucket logic decide where they belong
    return true;
  }, []);

  const hasFinanceRaisedDispute = React.useCallback((application) => {
    const w = application?.workflow || {};
    const fin = w.financialVerification || {};
    const depRaised = Array.isArray(fin.dependencies) && fin.dependencies.some(d => (d?.raisedBy?.department || d?.department) === 'financial' && (d.status === 'raised' || d.status === 'pending'));
    return application?.status === 'pending_financial_clarification' || depRaised;
  }, []);

  const hasFinanceQueryReply = React.useCallback((application) => {
    const w = application?.workflow || {};
    const fin = w.financialVerification || {};
    const replied = Array.isArray(fin.dependencies) && fin.dependencies.some(d => (d?.raisedBy?.department || d?.department) === 'financial' && (d.status === 'reply_received' || d.status === 'addressed'));
    return replied;
  }, []);

  const financeBucketKeysForApp = React.useCallback((application) => {
    const keys = [];
    const w = application?.workflow || {};
    const fin = w.financialVerification || {};
    const ds = w.dischargeSettlement || {};
    const enach = w.enach || w.eNach || {};
    const dispatch = w.dispatch || w.dispatches || {};
    const pod = w.proofOfDispatch || w.pod || {};
    const disb = w.disbursal || {};
    const mv = w.medicalVerification || {};

    // 0. Diagnosis Approved - BALANCED detection for medically approved applications
    // This bucket should show applications that have diagnosis approved but are not yet in advanced stages
    const diagnosisApproved = (
      // Primary medical approval indicators
      (mv.status === 'approved' || application.status === 'medical_approved') ||
      // Medical verification with approval indicators
      (mv.verifiedBy && mv.status !== 'rejected') ||
      (mv.verifiedAt && mv.status !== 'rejected') ||
      (mv.approvedBy && mv.status !== 'rejected') ||
      (mv.approvedAt && mv.status !== 'rejected') ||
      // KYP approval also indicates diagnosis approved
      (mv.kypApproved && mv.kypStatus !== 'rejected') ||
      (mv.kypStatus === 'approved') ||
      // Medical review completion
      (mv.status === 'completed' && mv.status !== 'rejected') ||
      (mv.reviewCompleted && mv.status !== 'rejected') ||
      // Application-level medical indicators
      (application.medicalApproved && application.status !== 'rejected') ||
      (application.diagnosisApproved && application.status !== 'rejected') ||
      // Workflow completion indicators
      (w.medicalVerification?.status === 'approved') ||
      (w.medicalVerification?.status === 'completed' && w.medicalVerification?.status !== 'rejected') ||
      // Doctor approval indicators
      (mv.doctorApproved && mv.status !== 'rejected') ||
      (mv.doctorVerified && mv.status !== 'rejected') ||
      // Specific medical statuses that indicate approval
      (mv.status && ['approved', 'verified', 'completed', 'accepted'].includes(mv.status)) ||
      // Medical review completed successfully
      (w.medicalReview && w.medicalReview.status === 'completed' && w.medicalReview.status !== 'rejected')
    );
    if (diagnosisApproved) keys.push('diagnosis_approved');

    // 1. Query raised by Finance - check multiple conditions
    const queryRaisedFinance = (
      application?.status === 'pending_financial_clarification' ||
      application?.status === 'dispute_pending' ||
      fin?.status === 'pending_info' ||
      fin?.status === 'under_review' ||
      (fin?.assessment?.status === 'rejected' && !fin?.status) ||
      hasFinanceRaisedDispute(application)
    );
    if (queryRaisedFinance) keys.push('query_raised_finance');

  // 2. Query reply received for Finance - ULTRA AGGRESSIVE detection
  const queryReplyFinance = (
    // Original logic
    (application?.status === 'under_review' && hasFinanceQueryReply(application)) ||
    (application?.status === 'under_review' && fin?.status === 'approved' && !hasFinanceQueryReply(application)) ||
    // ULTRA AGGRESSIVE - catch ANY application that has been approved after any query
    (fin?.status === 'approved' && (fin?.queryRaisedAt || fin?.disputeRaisedAt || fin?.clarificationRequestedAt)) ||
    // Catch applications that moved from any query state to approved
    (fin?.status === 'approved' && (fin?.previousStatus === 'pending_info' || fin?.previousStatus === 'query_raised' || fin?.previousStatus === 'dispute_raised')) ||
    // Catch any application with approved status that had any finance interaction
    (fin?.status === 'approved' && (fin?.verifiedBy || fin?.verifiedAt || fin?.assessment)) ||
    // Catch applications that are under review with any finance status
    (application?.status === 'under_review' && fin?.status === 'approved') ||
    // Catch any application with finance approval that had dependencies
    (fin?.status === 'approved' && Array.isArray(fin?.dependencies) && fin.dependencies.length > 0) ||
    // Catch applications with any reply indicators
    application?.queryReplyReceived || application?.financeReplyReceived || application?.disputeReplyReceived ||
    fin?.queryReplyReceived || fin?.replyReceived || fin?.responseReceived || fin?.hospitalResponseReceived ||
    // Catch any timestamp indicating reply
    fin?.replyReceivedAt || fin?.responseReceivedAt || fin?.hospitalReplyAt || fin?.lastReplyAt || fin?.lastResponseAt ||
    // Catch any status indicating reply
    fin?.responseStatus === 'received' || fin?.replyStatus === 'received' || fin?.hospitalResponseStatus === 'received' ||
    // Catch any workflow progression
    (fin?.status === 'approved' && (w?.queryProgress || w?.disputeProgress || w?.clarificationProgress)) ||
    // Catch any resolution indicators
    fin?.disputeResolved || fin?.queryResolved || fin?.clarificationResolved ||
    fin?.queryCompleted || fin?.disputeCompleted || fin?.clarificationCompleted ||
    // Catch any hospital response
    fin?.hospitalResponse || fin?.hospitalReply || fin?.hospitalFeedback ||
    // Catch any clarification response
    fin?.clarificationResponse || fin?.infoResponse || fin?.documentResponse ||
    // Catch any response indicators
    fin?.hasResponse || fin?.hasReply || fin?.hasFeedback ||
    // ULTRA FALLBACK - if finance is approved and there's any workflow, assume reply received
    (fin?.status === 'approved' && (w?.financialVerification || w?.dischargeSettlement || w?.enach || w?.dispatch || w?.proofOfDispatch || w?.disbursal))
  );
  
  if (queryReplyFinance) keys.push('query_reply_finance');

  // 3. Finance approved/reject - check multiple status fields
  const financeApproved = (
    (fin?.status || '').toLowerCase() === 'approved' ||
    application?.status === 'financial_approved' ||
    fin?.assessment?.status === 'approved' ||
    (fin?.verifiedBy !== null && fin?.verifiedBy !== undefined) ||
    (fin?.verifiedAt !== null && fin?.verifiedAt !== undefined)
  );
  if (financeApproved) keys.push('financial_approved');

    const financeRejected = (
      (fin?.status || '').toLowerCase() === 'rejected' ||
      application?.status === 'financial_rejected' ||
      fin?.assessment?.status === 'rejected' ||
      application?.status === 'rejected'
    );
    if (financeRejected) keys.push('financial_reject');

    // 4. FAL generated/accepted - check multiple fields
    const falGenerated = (
      !!ds?.finalApprovalLetterUrl ||
      !!ds?.finalApprovalLetterId ||
      ds?.status === 'computed' ||
      ds?.status === 'generated'
    );
    if (falGenerated) keys.push('fal_generated');

    const falAccepted = (
      ds?.hospitalAcknowledgement?.status === 'accepted' ||
    ds?.status === 'accepted' ||
    ds?.hospitalAcknowledgedAt ||
    ds?.acknowledgedAt ||
    ds?.hospitalAcknowledgedBy ||
    (Array.isArray(w?.sanctions) && w.sanctions.some(s => s?.hospitalAcknowledgement?.status === 'accepted')) ||
    (Array.isArray(w?.acknowledgements) && w.acknowledgements[ds?.finalApprovalLetterId]?.status === 'accepted') ||
    // Creative FAL accepted detection
    ds?.hospitalAccepted ||
    ds?.accepted ||
    ds?.hospitalAcceptedAt ||
    ds?.acceptedAt ||
    application?.falAccepted ||
    application?.falHospitalAccepted ||
    application?.falStatus === 'accepted' ||
    application?.falHospitalStatus === 'accepted' ||
    // Check if FAL was generated and hospital has responded positively
    (falGenerated && (ds?.hospitalResponse === 'accepted' || ds?.hospitalFeedback === 'accepted' || ds?.hospitalStatus === 'accepted')) ||
    // Check if there's any positive acknowledgment
    (ds?.acknowledgement && (ds.acknowledgement.status === 'accepted' || ds.acknowledgement.hospitalStatus === 'accepted')) ||
    // Check if FAL has been viewed/opened by hospital (indicates acceptance)
    (ds?.hospitalViewedAt || ds?.hospitalOpenedAt || ds?.hospitalAccessedAt) ||
    // Check if there's any positive response in the workflow
    (w?.falAcceptance && w.falAcceptance.status === 'accepted') ||
    // Check if application status indicates FAL acceptance
    application?.status === 'fal_accepted' ||
    application?.status === 'fal_acknowledged' ||
    application?.status === 'fal_hospital_accepted' ||
    // Check if FAL is generated and no rejection indicators
    (falGenerated && !ds?.hospitalAcknowledgement?.status?.includes('reject') && !ds?.hospitalAcknowledgement?.status?.includes('challenge')) ||
    // Check if FAL is generated and hospital has any positive response
    (falGenerated && (ds?.hospitalResponse || ds?.hospitalFeedback || ds?.hospitalStatus)) ||
    // Check if FAL is generated and no negative indicators
    (falGenerated && !ds?.hospitalAcknowledgement?.status?.includes('pending') && !ds?.hospitalAcknowledgement?.status?.includes('waiting'))
    );
    if (falAccepted) keys.push('fal_accepted');

  // 5. eNach - MUTUALLY EXCLUSIVE logic
  const enachCompleted = (
    enach?.status === 'completed' || 
    enach?.status === 'success' ||
    enach?.hospitalCompletedAt ||
    enach?.completedAt ||
    enach?.hospitalAcknowledgedAt
  );
  
  const enachPending = (
    !enachCompleted && (
      enach?.status === 'pending' || 
      enach?.status === 'initiated' ||
      enach?.status === 'requested' ||
      (falAccepted && !enach?.hospitalCompletedAt && !enach?.status) ||
      (falAccepted && enach?.status !== 'completed') ||
      // Creative eNach pending detection
      application?.enachPending ||
      application?.enachStatus === 'pending' ||
      application?.enachStatus === 'initiated' ||
      (falAccepted && !enach?.completedAt && !enach?.hospitalCompletedAt) ||
      // Check if FAL is accepted but eNach not yet completed
      (falAccepted && !enach?.hospitalAcknowledgedAt && !enach?.acknowledgedAt) ||
      // Check if there's any eNach workflow but not completed
      (enach && !enach?.status && !enach?.completedAt) ||
      // Check if application status indicates eNach pending
      application?.status === 'enach_pending' ||
      application?.status === 'enach_initiated' ||
      application?.status === 'enach_requested' ||
      // Check if FAL is accepted and eNach is in progress
      (falAccepted && enach?.status && !enach?.status.includes('completed') && !enach?.status.includes('success')) ||
      // Check if eNach is in any non-completed state
      (enach?.status && !enach?.status.includes('completed') && !enach?.status.includes('success') && !enach?.status.includes('failed'))
    )
  );
  
  if (enachCompleted) keys.push('enach_completed');
  if (enachPending) keys.push('enach_pending');

    // 6. Scrutiny - check dispatch and scrutiny status
    const scrutinyPending = (
      dispatch?.status === 'pending' ||
      ds?.scrutinySubmission?.status === 'submitted' ||
      ds?.scrutinyChecklist?.some(item => item.status === 'pending') ||
      ds?.dispatchReview?.status === 'requested'
    );
    if (scrutinyPending) keys.push('scrutiny_pending');

    const scrutinyCompleted = (
      dispatch?.status === 'completed' ||
      ds?.dispatchClearance?.status === 'approved' ||
      ds?.scrutinyReview?.status === 'checker_confirmed'
    );
    if (scrutinyCompleted) keys.push('scrutiny_completed');

  // 7. POD (Proof of Dispatch) - MUTUALLY EXCLUSIVE logic
  const podCompleted = (
    pod?.status === 'completed' || 
    pod?.status === 'received' ||
    pod?.acknowledgedAt ||
    pod?.hospitalAcknowledgedAt ||
    pod?.acknowledgedBy
  );
  
  const podPending = (
    !podCompleted && (
      pod?.status === 'pending' || 
      pod?.status === 'initiated' ||
      pod?.status === 'requested' ||
      (dispatch?.status === 'completed' && !pod?.acknowledgedAt) ||
      (Array.isArray(application?.dispatchDocuments) && application.dispatchDocuments.length > 0 && !pod?.acknowledgedAt) ||
      // Creative POD pending detection
      application?.podPending ||
      application?.podStatus === 'pending' ||
      application?.podStatus === 'initiated' ||
      // Check if dispatch is completed but POD not acknowledged
      (dispatch?.status === 'completed' && !pod?.completedAt && !pod?.hospitalAcknowledgedAt) ||
      // Check if there are dispatch documents but no POD acknowledgment
      (Array.isArray(application?.dispatchDocuments) && application.dispatchDocuments.length > 0 && !pod?.completedAt) ||
      // Check if application status indicates POD pending
      application?.status === 'pod_pending' ||
      application?.status === 'pod_initiated' ||
      application?.status === 'pod_requested' ||
      // Check if POD is in any non-completed state
      (pod?.status && !pod?.status.includes('completed') && !pod?.status.includes('success') && !pod?.status.includes('received')) ||
      // Check if dispatch is completed and POD exists but not completed
      (dispatch?.status === 'completed' && pod && !pod?.acknowledgedAt && !pod?.completedAt)
    )
  );
  
  if (podCompleted) keys.push('pod_completed');
  if (podPending) keys.push('pod_pending');

    // 8. Disbursement - ULTRA COMPREHENSIVE detection
    const disbursedPending = (
      // Basic status checks
      disb?.status === 'pending' || 
      disb?.status === 'initiated' ||
      disb?.status === 'processing' ||
      disb?.status === 'in_progress' ||
      // Check if disbursement is requested but not completed
      (disb && !disb?.completedAt && !disb?.status?.includes('completed')) ||
      // Check if POD is completed but disbursement not yet done
      (podCompleted && !disb?.completedAt) ||
      // Check if there are disbursement documents but not completed
      (Array.isArray(application?.disbursementDocuments) && application.disbursementDocuments.length > 0 && !disb?.completedAt) ||
      // Check if application status indicates disbursement pending
      application?.disbursementPending ||
      application?.disbursementStatus === 'pending' ||
      application?.disbursementStatus === 'initiated' ||
      application?.disbursementStatus === 'processing' ||
      application?.status === 'disbursement_pending' ||
      application?.status === 'disbursement_initiated' ||
      application?.status === 'disbursement_processing' ||
      // Check if there's any disbursement workflow but not completed
      (disb && disb?.status && !disb?.status.includes('completed') && !disb?.status.includes('success')) ||
      // Check if disbursement is in any non-completed state
      (disb?.status && !disb?.status.includes('completed') && !disb?.status.includes('success') && !disb?.status.includes('failed')) ||
      // Check if POD is completed and disbursement exists but not completed
      (podCompleted && disb && !disb?.completedAt && !disb?.status?.includes('completed')) ||
      // Check if there are any disbursement indicators but not completed
      (disb?.initiatedAt && !disb?.completedAt) ||
      (disb?.requestedAt && !disb?.completedAt) ||
      (disb?.processingAt && !disb?.completedAt) ||
      // Check if disbursement has any workflow but not completed
      (disb && (disb?.workflow || disb?.process || disb?.steps) && !disb?.completedAt) ||
      // Check if there are any disbursement timestamps but not completed
      (disb?.startedAt && !disb?.completedAt) ||
      (disb?.createdAt && !disb?.completedAt) ||
      // ULTRA FALLBACK - if POD is completed, assume disbursement is pending
      (podCompleted && !disb?.completedAt)
    );
    
    const disbursedCompleted = (
      // Basic completion checks
      disb?.status === 'completed' || 
      disb?.status === 'success' ||
      disb?.status === 'disbursed' ||
      disb?.completedAt ||
      disb?.disbursedAt ||
      disb?.successAt ||
      // Check if disbursement is marked as done
      disb?.status?.includes('completed') ||
      disb?.status?.includes('success') ||
      disb?.status?.includes('disbursed') ||
      // Check if application status indicates disbursement completed
      application?.disbursementCompleted ||
      application?.disbursementStatus === 'completed' ||
      application?.disbursementStatus === 'success' ||
      application?.disbursementStatus === 'disbursed' ||
      application?.status === 'disbursement_completed' ||
      application?.status === 'disbursement_success' ||
      application?.status === 'disbursement_disbursed' ||
      // Check if there are completion indicators
      disb?.finalizedAt ||
      disb?.processedAt ||
      disb?.executedAt ||
      disb?.transferredAt ||
      // Check if disbursement has any completion workflow
      (disb && (disb?.completed || disb?.finalized || disb?.processed)) ||
      // Check if there are any completion timestamps
      disb?.finishedAt ||
      disb?.endAt ||
      disb?.closeAt ||
      // ULTRA FALLBACK - if disbursement exists and has any positive status
      (disb && disb?.status && (disb.status.includes('completed') || disb.status.includes('success') || disb.status.includes('done')))
    );
    
    if (disbursedPending) keys.push('disbursed_pending');
    if (disbursedCompleted) keys.push('disbursed');


  // 10. New applications - SIMPLE and WORKING detection
  const isNewApplication = (
    // SIMPLE - if no other buckets match, it's new
    keys.length === 0
  );
  
  if (isNewApplication) {
    keys.push('new');
  }

    return keys;
  }, [hasFinanceRaisedDispute, hasFinanceQueryReply]);

  // Choose a single canonical bucket for counting so sums match 'All'
  // Note: This function is kept for backward compatibility but is no longer used in counting
  const financePrimaryBucketForApp = React.useCallback((application) => {
    const keys = financeBucketKeysForApp(application);
    // Priority order from most terminal to earliest stage
    const priority = [
      'disbursed',
      'disbursed_pending',
      'pod_completed',
      'pod_pending',
      'scrutiny_completed',
      'scrutiny_pending',
      'enach_completed',
      'enach_pending',
      'fal_accepted',
      'fal_generated',
      'financial_approved',
      'financial_reject',
      'query_reply_finance',
      'query_raised_finance',
      'diagnosis_approved',
      'new',
    ];
    for (const p of priority) {
      if (keys.includes(p)) return p;
    }
    return 'new';
  }, [financeBucketKeysForApp]);

  const buildFinanceStageCounts = React.useCallback((applications) => {
    const counts = {
      all: 0,
      new: 0,
      diagnosis_approved: 0,
      query_raised_finance: 0,
      query_reply_finance: 0,
      financial_approved: 0,
      financial_reject: 0,
      fal_generated: 0,
      fal_accepted: 0,
      enach_pending: 0,
      enach_completed: 0,
      scrutiny_pending: 0,
      scrutiny_completed: 0,
      pod_pending: 0,
      pod_completed: 0,
      disbursed_pending: 0,
      disbursed: 0,
    };
    applications.forEach((a) => {
      counts.all += 1;
      // Count all buckets the application belongs to, not just the primary one
      const keys = financeBucketKeysForApp(a);
      keys.forEach(key => {
        counts[key] = (counts[key] || 0) + 1;
      });
    });
    return counts;
  }, [financeBucketKeysForApp]);

  const matchesFinanceBucket = React.useCallback((application, bucketKey) => {
    if (!bucketKey || bucketKey === 'all') {
      // STRICT "All applications" logic - show ONLY valid finance applications
      return (
        // ONLY show applications that are in any finance stage
        financeBucketKeysForApp(application).length > 0
      );
    }
    const keys = new Set(financeBucketKeysForApp(application));
    return keys.has(bucketKey);
  }, [financeBucketKeysForApp]);

  // Load All Applications for finance team
  const loadAllApplications = useCallback(async () => {
    setAllLoading(true);
    try {
      const filters = { searchTerm: allSearch, _fetchAll: true };
      const result = await getAllApplicationsAdmin(
        filters,
        allPage,
        10
      );
      if (result.success) {
        const raw = (result.applications || []).filter(isFinanceDomainApplication);
        const counts = buildFinanceStageCounts(raw);
        const filtered = raw.filter((a) => matchesFinanceBucket(a, allFinanceStage));
        setAllApplicationsRaw(raw);
        setAllApplications(filtered);
        setAllTotalPages(result.totalPages || 1);
        setAllFinanceStageCounts(counts);
      } else {
        setAllApplications([]);
        setAllTotalPages(1);
        setAllFinanceStageCounts({});
      }
    } catch (err) {
      console.error('Error loading all applications (finance):', err);
      setAllApplications([]);
      setAllTotalPages(1);
      setAllFinanceStageCounts({});
    } finally {
      setAllLoading(false);
    }
  }, [allSearch, allPage, allFinanceStage, buildFinanceStageCounts, matchesFinanceBucket, isFinanceDomainApplication]);

  

  // Challenged (KYP reassigned to Finance)
  const [challengedApps, setChallengedApps] = useState([]);
  const [challengedLoading, setChallengedLoading] = useState(false);


  const loadChallenged = useCallback(async () => {
    setChallengedLoading(true);
    try {
      const result = await getAllApplicationsAdmin(
        { kypChallengedTo: 'finance' },
        1,
        20
      );
      setChallengedApps(result.success ? (result.applications || []) : []);
    } catch (e) {
      console.error('Error loading challenged (finance):', e);
      setChallengedApps([]);
    } finally {
      setChallengedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'challenged') {
      loadChallenged();
    }
  }, [activeSection, loadChallenged]);

  const renderChallenged = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Challenged KYP</h1>
          <p className="text-gray-600">Applications reassigned by doctors for finance review</p>
        </div>
        <button onClick={loadChallenged} className="btn-outline"><RefreshCw className="h-4 w-4 mr-2"/>Refresh</button>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {challengedLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mr-2"></div>
            <span className="text-gray-600">Loading challenged cases...</span>
          </div>
        ) : challengedApps.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No challenged cases</h3>
            <p className="text-gray-600">Nothing to review right now.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {challengedApps.map((application) => (
              <div key={application.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/dashboard/financial/review/${application.applicationId}`)}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-900">{application.patient?.name || 'Patient'}</div>
                    <div className="text-xs text-gray-500">{application.applicationId}</div>
                    {application.workflow?.medicalVerification?.kypReassignment?.comment && (
                      <div className="mt-1 text-xs text-gray-600">Note: {application.workflow.medicalVerification.kypReassignment.comment}</div>
                    )}
                  </div>
                  <div className="text-right text-sm text-gray-700">
                    <div className="flex items-center justify-end gap-2">
                      <Building className="h-3 w-3 text-gray-400" /> {application.hospitalName || '—'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Reassigned on {application.workflow?.medicalVerification?.kypReassignment?.at ? new Date(application.workflow.medicalVerification.kypReassignment.at.seconds ? application.workflow.medicalVerification.kypReassignment.at.seconds*1000 : application.workflow.medicalVerification.kypReassignment.at).toLocaleString() : '—'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );


  // Trigger load when entering the section or when search/page changes
  useEffect(() => {
  if (activeSection === 'all-applications') {
      loadAllApplications();
    }
  }, [activeSection, loadAllApplications]);

  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const getEfficiencyScore = () => {
    const processingScore = dashboardStats.averageProcessingTime > 0 
      ? Math.max(10, 100 - dashboardStats.averageProcessingTime) 
      : 100;
    const approvalScore = dashboardStats.approvalRate;
    const disbursalScore = dashboardStats.averageDisbursalRatio;
    
    return Math.round((processingScore + approvalScore + disbursalScore) / 3);
  };

  // removed unused getEfficiencyColor

  const renderNotifications = () => {
    if (notifications.length === 0) return null;

    return (
      <div className="mb-6 space-y-3">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`p-4 rounded-lg border-l-4 ${
              notification.type === 'urgent' 
                ? 'bg-red-50 border-red-400 text-red-800'
                : 'bg-yellow-50 border-yellow-400 text-yellow-800'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Bell className="h-4 w-4" />
                <span className="text-sm font-medium">{notification.message}</span>
              </div>
              {notification.action && (
                <button
                  onClick={notification.action}
                  className="text-sm font-medium underline hover:no-underline"
                >
                  Take Action
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header with Real-time Updates */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Financial Team Dashboard</h1>
          <div className="flex items-center space-x-4 mt-1">
            <p className="text-gray-600">Manage KYC, eligibility, disbursals and recovery tracking</p>
            {lastUpdated && (
              <div className="flex items-center space-x-1 text-xs text-gray-500">
                <Activity className="h-3 w-3" />
                <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 shadow-soft"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            <span>Refresh</span>
          </button>
          {/* Removed compact status pill */}
        </div>
      </div>

      {/* Notifications */}
      {renderNotifications()}

      {/* Enhanced Stats Cards with Trends */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Pending Processing with Emergency Indicator */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6 relative">
          {dashboardStats.emergencyPending > 0 && (
            <div className="absolute top-2 right-2">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Pending Processing
              </p>
              <p className="text-2xl font-bold text-orange-600">
                {dashboardStats.pendingProcessing}
              </p>
              {dashboardStats.emergencyPending > 0 && (
                <p className="text-xs text-red-600 mt-1 font-medium">
                  {dashboardStats.emergencyPending} Emergency cases
                </p>
              )}
            </div>
            <div className="bg-orange-100 p-3 rounded-lg">
              <Clock className="h-6 w-6 text-orange-600" />
            </div>
          </div>
          {dashboardStats.pendingProcessing > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <button
                onClick={() => setActiveSection("applications")}
                className="text-xs text-orange-600 hover:text-orange-700 font-medium flex items-center space-x-1"
              >
                <span>Process Now</span>
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>

        {/* Processed Today with Performance Indicator */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Processed Today
              </p>
              <p className="text-2xl font-bold text-blue-600">
                {dashboardStats.processedToday}
              </p>
              <div className="flex items-center space-x-2 mt-1">
                <p className="text-xs text-gray-500">
                  Avg: {dashboardStats.averageProcessingTime} min/case
                </p>
                {dashboardStats.averageProcessingTime < 30 && (
                  <TrendingUp className="h-3 w-3 text-green-500" />
                )}
                {dashboardStats.averageProcessingTime > 60 && (
                  <TrendingDown className="h-3 w-3 text-red-500" />
                )}
              </div>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Approval Rate with Target Comparison */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Approval Rate</p>
              <p className="text-2xl font-bold text-green-600">
                {dashboardStats.approvalRate}%
              </p>
              <div className="flex items-center space-x-1 mt-1">
                <p className="text-xs text-gray-500">
                  {dashboardStats.approvedCount} / {dashboardStats.totalProcessed}
                </p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  dashboardStats.approvalRate >= 80 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  Target: 80%
                </span>
              </div>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Amount Disbursed with Monthly Comparison */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Amount Disbursed
              </p>
              <p className="text-2xl font-bold text-purple-600">
                {formatAmount(dashboardStats.totalDisbursedAmount)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Efficiency: {dashboardStats.averageDisbursalRatio}% of claims
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Disbursals Pending with Action */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Disbursals Pending
              </p>
              <p className="text-2xl font-bold text-yellow-600">
                {dashboardStats.disbursalsPending}
              </p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-lg">
              <CreditCard className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
          {dashboardStats.disbursalsPending > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <button
                onClick={() => setActiveSection("disbursal")}
                className="text-xs text-yellow-600 hover:text-yellow-700 font-medium flex items-center space-x-1"
              >
                <span>Track Disbursals</span>
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>

        {/* Recovery Pending */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Recovery Pending
              </p>
              <p className="text-2xl font-bold text-indigo-600">
                {dashboardStats.recoveryPending}
              </p>
            </div>
            <div className="bg-indigo-100 p-3 rounded-lg">
              <Banknote className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
          {dashboardStats.recoveryPending > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <button
                onClick={() => setActiveSection("recovery")}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center space-x-1"
              >
                <span>Track Recovery</span>
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>

        {/* Total Claims */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Claims</p>
              <p className="text-2xl font-bold text-red-600">
                {formatAmount(dashboardStats.totalClaimAmount)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Processed volume
              </p>
            </div>
            <div className="bg-red-100 p-3 rounded-lg">
              <Receipt className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        {/* Overall Efficiency Score */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Efficiency Score
              </p>
              <p className={`text-2xl font-bold ${
                getEfficiencyScore() >= 80 ? 'text-green-600' :
                getEfficiencyScore() >= 60 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {getEfficiencyScore()}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Overall performance
              </p>
            </div>
            <div className={`p-3 rounded-lg ${
              getEfficiencyScore() >= 80 ? 'bg-green-100' :
              getEfficiencyScore() >= 60 ? 'bg-yellow-100' : 'bg-red-100'
            }`}>
              <Target className={`h-6 w-6 ${
                getEfficiencyScore() >= 80 ? 'text-green-600' :
                getEfficiencyScore() >= 60 ? 'text-yellow-600' : 'text-red-600'
              }`} />
            </div>
          </div>
        </div>
      </div>

      {/* Rest of your existing dashboard content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Processing Status Breakdown */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Processing Status Breakdown
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Approved</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-gray-900">
                  {dashboardStats.approvedCount}
                </span>
                <span className="text-xs text-gray-500 ml-1">
                  (
                  {dashboardStats.totalProcessed > 0
                    ? Math.round(
                        (dashboardStats.approvedCount /
                          dashboardStats.totalProcessed) *
                          100
                      )
                    : 0}
                  %)
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Rejected</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-gray-900">
                  {dashboardStats.rejectedCount}
                </span>
                <span className="text-xs text-gray-500 ml-1">
                  (
                  {dashboardStats.totalProcessed > 0
                    ? Math.round(
                        (dashboardStats.rejectedCount /
                          dashboardStats.totalProcessed) *
                          100
                      )
                    : 0}
                  %)
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Pending Info</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-gray-900">
                  {dashboardStats.pendingInfoCount}
                </span>
                <span className="text-xs text-gray-500 ml-1">
                  (
                  {dashboardStats.totalProcessed > 0
                    ? Math.round(
                        (dashboardStats.pendingInfoCount /
                          dashboardStats.totalProcessed) *
                          100
                      )
                    : 0}
                  %)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Quick Actions */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Actions
          </h3>
          <div className="space-y-3">

            <button
              onClick={() => setActiveSection("settlement")}
              className="w-full flex items-center justify-between p-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <Calculator className="h-4 w-4" />
                <span className="text-sm font-medium">
                  Discharge Settlement
                </span>
              </div>
              <ArrowRight className="h-4 w-4" />
            </button>

            <button
              onClick={() => setActiveSection("disbursal")}
              className="w-full flex items-center justify-between p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <CreditCard className="h-4 w-4" />
                <span className="text-sm font-medium">Disbursal Tracking</span>
              </div>
              <div className="flex items-center space-x-1">
                {dashboardStats.disbursalsPending > 0 && (
                  <span className="bg-blue-200 text-blue-800 text-xs px-2 py-1 rounded-full">
                    {dashboardStats.disbursalsPending}
                  </span>
                )}
                <ArrowRight className="h-4 w-4" />
              </div>
            </button>

            {dashboardStats.emergencyPending > 0 && (
              <button
                onClick={() => setActiveSection("applications")}
                className="w-full flex items-center justify-between p-3 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors animate-pulse"
              >
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm font-medium">Emergency Cases</span>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="bg-red-200 text-red-800 text-xs px-2 py-1 rounded-full">
                    {dashboardStats.emergencyPending}
                  </span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Enhanced Performance Metrics */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Performance Metrics
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-600">
                  Processing Speed
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {dashboardStats.averageProcessingTime > 0
                    ? Math.max(10, 100 - dashboardStats.averageProcessingTime)
                    : 100}
                  /100
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    dashboardStats.averageProcessingTime < 30 
                      ? 'bg-green-500' 
                      : dashboardStats.averageProcessingTime < 60
                      ? 'bg-yellow-500'
                      : 'bg-red-500'
                  }`}
                  style={{
                    width: `${
                      dashboardStats.averageProcessingTime > 0
                        ? Math.max(
                            10,
                            100 - dashboardStats.averageProcessingTime
                          )
                        : 100
                    }%`,
                  }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-600">Approval Rate</span>
                <span className="text-sm font-medium text-gray-900">
                  {dashboardStats.approvalRate}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    dashboardStats.approvalRate >= 80 
                      ? 'bg-green-500' 
                      : dashboardStats.approvalRate >= 60
                      ? 'bg-yellow-500'
                      : 'bg-red-500'
                  }`}
                  style={{ width: `${dashboardStats.approvalRate}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-600">
                  Disbursal Efficiency
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {dashboardStats.averageDisbursalRatio}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    dashboardStats.averageDisbursalRatio >= 85 
                      ? 'bg-green-500' 
                      : dashboardStats.averageDisbursalRatio >= 70
                      ? 'bg-yellow-500'
                      : 'bg-red-500'
                  }`}
                  style={{ width: `${dashboardStats.averageDisbursalRatio}%` }}
                ></div>
              </div>
            </div>

            <div className="pt-2 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Processed</span>
                <span className="text-lg font-bold text-gray-900">
                  {dashboardStats.totalProcessed}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Recent Activity Section */}
      <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Priority Processing
          </h3>
          <div className="flex items-center space-x-3">
            {dashboardStats.pendingProcessing > 0 && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">{dashboardStats.pendingProcessing}</span> applications waiting
              </div>
            )}
            <button
              onClick={() => setActiveSection("applications")}
              className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center space-x-1"
            >
              <span>View All</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {dashboardStats.pendingProcessing > 0 ? (
          <div className="text-center py-8">
            <div className="relative">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              {dashboardStats.emergencyPending > 0 && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-xs text-white font-bold">!</span>
                </div>
              )}
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              {dashboardStats.pendingProcessing} Applications Awaiting Financial Review
            </h4>
            <p className="text-gray-600 mb-4">
              Process KYC verification and eligibility assessment for faster disbursals
              {dashboardStats.emergencyPending > 0 && (
                <span className="block text-red-600 font-medium mt-1">
                  {dashboardStats.emergencyPending} emergency cases need immediate attention!
                </span>
              )}
            </p>
            <div className="flex justify-center space-x-3">
              <button
                onClick={() => setActiveSection("applications")}
                className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2 shadow-soft"
              >
                <FileText className="h-4 w-4" />
                <span>Start KYC Review</span>
              </button>
              {dashboardStats.emergencyPending > 0 && (
                <button
                  onClick={() => setActiveSection("applications")}
                  className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2 shadow-soft animate-pulse"
                >
                  <AlertTriangle className="h-4 w-4" />
                  <span>Emergency First</span>
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              All Caught Up!
            </h4>
            <p className="text-gray-600 mb-4">
              No pending financial processing at the moment
            </p>
            <div className="flex justify-center space-x-3">
              <button
                onClick={() => setActiveSection("settlement")}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm"
              >
                Process Settlements
              </button>
              <button
                onClick={() => setActiveSection("disbursal")}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                Track Disbursals
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Sort state for all applications
  const [allSortField, setAllSortField] = useState('submittedAt');
  const [allSortDirection, setAllSortDirection] = useState('desc');

  const handleSortChange = (field) => {
    if (allSortField === field) {
      setAllSortDirection(allSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setAllSortField(field);
      setAllSortDirection('desc');
    }
  };

  const renderAllApplications = () => {
    // Sort applications
    const sortedApps = [...allApplications].sort((a, b) => {
      let aVal, bVal;
      switch (allSortField) {
        case 'applicationId':
          aVal = a.applicationId || '';
          bVal = b.applicationId || '';
          return aVal.localeCompare(bVal);
        case 'patient.name':
          aVal = a.patient?.name || '';
          bVal = b.patient?.name || '';
          return aVal.localeCompare(bVal);
        case 'amount':
          aVal = a.policy?.coverageAmount || a.medical?.estimatedCost || 0;
          bVal = b.policy?.coverageAmount || b.medical?.estimatedCost || 0;
          return aVal - bVal;
        case 'submittedAt':
        default:
          aVal = a.submittedAt?.toDate ? a.submittedAt.toDate() : (a.submittedAt ? new Date(a.submittedAt) : new Date(0));
          bVal = b.submittedAt?.toDate ? b.submittedAt.toDate() : (b.submittedAt ? new Date(b.submittedAt) : new Date(0));
          return bVal - aVal;
      }
    });

    if (allSortDirection === 'asc') {
      sortedApps.reverse();
    }

    return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">All Applications</h1>
          <p className="text-gray-600">View every application across the system</p>
        </div>
        <div className="flex items-center space-x-3">
          <input
            type="text"
            placeholder="Search by ID, patient, diagnosis, hospital, doctor"
            value={allSearch}
            onChange={(e) => {
              setAllPage(1);
              setAllSearch(e.target.value);
            }}
            className="input-field w-72"
          />
          <button onClick={() => { setAllSearch(''); setAllPage(1); }} className="btn-outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </button>
        </div>
      </div>

  {/* Finance bifurcation tabs (Finance-only, sticky) */}
  {/**
   * Tabs limited to Finance workflow only; Query buckets are Finance-specific.
   */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-16 z-30">
        <div className="px-4 pt-3 pb-3 overflow-x-auto">
          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: 'all', label: 'All' },
              { key: 'new', label: 'New applications' },
              { key: 'diagnosis_approved', label: 'Diagnosis approved' },
              { key: 'query_raised_finance', label: 'Query raised' },
              { key: 'query_reply_finance', label: 'Query reply received' },
              { key: 'financial_approved', label: 'Finance approved' },
              { key: 'financial_reject', label: 'Finance reject' },
              { key: 'fal_generated', label: 'FAL generated' },
              { key: 'fal_accepted', label: 'FAL accepted' },
              { key: 'enach_pending', label: 'E-NACH pending' },
              { key: 'enach_completed', label: 'E-NACH completed' },
              { key: 'scrutiny_pending', label: 'Scrutiny pending' },
              { key: 'scrutiny_completed', label: 'Scrutiny completed' },
              { key: 'pod_pending', label: 'POD pending' },
              { key: 'pod_completed', label: 'POD completed' },
              { key: 'disbursed_pending', label: 'Disbursed pending' },
              { key: 'disbursed', label: 'Disbursed' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setAllFinanceStage(tab.key);
                  setAllPage(1);
                  // Re-filter client-side from raw to avoid extra reads
                  setAllApplications(allApplicationsRaw.filter((a) => matchesFinanceBucket(a, tab.key)));
                }}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  allFinanceStage === tab.key
                    ? 'bg-teal-50 text-teal-700 border-teal-200'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span>{tab.label}</span>
                {typeof allFinanceStageCounts[tab.key] === 'number' && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                    {allFinanceStageCounts[tab.key]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

  {/* List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {allLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mr-2"></div>
            <span className="text-gray-600">Loading applications...</span>
          </div>
        ) : allApplications.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications Found</h3>
            <p className="text-gray-600">Try adjusting your search.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSortChange('applicationId')}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>Application</span>
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSortChange('patient.name')}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>Patient</span>
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hospital</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Policy Details</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSortChange('amount')}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>Amount</span>
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSortChange('submittedAt')}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>Submitted</span>
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Life Cycle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedApps.map((application) => (
                    <tr
                      key={application.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={(e) => {
                        if (e.ctrlKey || e.metaKey) {
                          e.preventDefault();
                          e.stopPropagation();
                          const url = `${window.location.origin}/dashboard/financial/review/${application.applicationId}`;
                          window.open(url, '_blank', 'noopener,noreferrer');
                          return;
                        }
                        navigate(`/dashboard/financial/review/${application.applicationId}`);
                      }}
                    >
                      {/* Application */}
                      <td className="px-6 py-4 whitespace-normal">
                        <div className="flex items-center space-x-2">
                          <div>
                            <ClampedText className="text-sm font-medium text-gray-900" value={application.applicationId} />
                          </div>
                        </div>
                      </td>

                      {/* Patient */}
                      <td className="px-6 py-4 whitespace-normal">
                        <div className="flex items-center">
                          <div className="bg-gray-100 rounded-full p-2 mr-3">
                            <User className="h-4 w-4 text-gray-600" />
                          </div>
                          <div>
                            <ClampedText className="text-sm font-medium text-gray-900" value={application.patient?.name || 'N/A'} />
                            <ClampedText className="text-sm text-gray-500" value={`${application.patient?.age} years, ${application.patient?.gender}`} />
                          </div>
                        </div>
                      </td>

                      {/* Hospital */}
                      <td className="px-6 py-4 whitespace-normal">
                        <div className="flex items-center">
                          <div className="bg-blue-100 rounded-full p-2 mr-3">
                            <Building className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <ClampedText className="text-sm font-medium text-gray-900" value={application.hospitalName} />
                          </div>
                        </div>
                      </td>

                      {/* Policy Details */}
                      <td className="px-6 py-4 whitespace-normal">
                        <div>
                          <ClampedText className="text-sm font-medium text-gray-900" value={application.policy?.insuranceCompany || '—'} />
                          <ClampedText className="text-sm text-gray-500 font-mono" value={application.policy?.number || '—'} />
                        </div>
                      </td>

                      {/* Amount */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatAmount(application.policy?.coverageAmount)}
                          </div>
                          <div className="text-sm text-gray-500">
                            Claim: {formatAmount(application.medical?.estimatedCost)}
                          </div>
                        </div>
                      </td>

                      {/* Submitted */}
                      <td className="px-6 py-4 whitespace-normal">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <ClampedText className="text-sm text-gray-900" value={application.submittedAt?.toDate ? application.submittedAt.toDate().toLocaleDateString() : (application.submittedAt ? new Date(application.submittedAt).toLocaleDateString() : '—')} />
                            <ClampedText className="text-xs text-gray-500" value={application.submittedAt ? (() => { const submittedDate = application.submittedAt.toDate ? application.submittedAt.toDate() : new Date(application.submittedAt); const daysDiff = Math.floor((new Date() - submittedDate) / (1000 * 60 * 60 * 24)); return `${daysDiff} day${daysDiff !== 1 ? "s" : ""} ago`; })() : ''} />
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border bg-blue-100 text-blue-800 border-blue-200">
                          {application.status?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                        </span>
                      </td>

                      {/* Life Cycle */}
                      <td className="px-6 py-4 whitespace-normal">
                        {renderLifecycleChips(application)}
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-normal text-right text-sm font-medium">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/financial/review/${application?.applicationId}`)
                          }}
                          className="flex items-center space-x-1 text-teal-600 hover:text-teal-800 transition-colors"
                        >
                          <Eye className="h-4 w-4" />
                          <span>Review</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {allTotalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">Page {allPage} of {allTotalPages}</div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setAllPage((p) => Math.max(p - 1, 1))}
                      disabled={allPage === 1}
                      className="btn-outline text-sm"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setAllPage((p) => Math.min(p + 1, allTotalPages))}
                      disabled={allPage === allTotalPages}
                      className="btn-outline text-sm"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    );
  };

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return renderDashboard();
      case "applications":
        return <FinancialApplicationsList />;
      case "all-applications":
        return renderAllApplications();
      case "challenged":
        return renderChallenged();
      case "pending-data":
        return <PendingDataTab />;
      case "settlement":
        return <DischargeSettlement />;
      case "disbursal":
        return <DisbursalTracking />;
      case "enach":
        return <ENachProcessList />;
      case "dispatches":
        return <DispatchTracking />;
      case "recovery":
        return <RecoveryTracking />;
      case "history":
        return <FinancialHistory />;
      case "profile":
        return <FinancialProfile />;
      default:
        return renderDashboard();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading financial dashboard...</p>
          <p className="text-sm text-gray-500 mt-1">Fetching real-time data from Firebase</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      {/* Enhanced Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="flex items-center px-6 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-gray-600 hover:text-gray-900 lg:hidden"
            >
              {sidebarOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>

            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-3">
                <img src={samhealthLogo} alt="SamHealth" className="h-8 w-auto object-contain" />
                <p className="text-sm text-teal-600">Financial Team Portal</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4 flex-1 justify-end">
            {/* Universal Search Bar */}
            <div className="w-64 hidden md:block">
              <UniversalSearch userRole="finance" />
            </div>

            <div className="flex items-center space-x-4 flex-shrink-0">
              {/* Unified Notification Bell */}
              <NotificationBell />

            {/* Live indicator */}
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Live</span>
            </div>

            <div className="text-right">
              <p className="text-sm font-medium text-gray-800">
                {user?.profile?.name ||
                  user?.email?.split("@")[0] ||
                  "Financial Team"}
              </p>
              <p className="text-xs text-gray-600">
                {user?.profile?.designation || "Financial Processing"}
              </p>
            </div>
            <RoleSwitcher buttonClassName="border-gray-300 hover:border-teal-400 hover:text-teal-700" />
            {/* Removed decorative header icon */}
            <SignOutIconButton />
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Enhanced Sidebar */}
        <aside
          className={`${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } fixed lg:sticky lg:top-16 lg:translate-x-0 z-30 w-64 h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] bg-white/95 backdrop-blur-sm shadow-lg border-r border-gray-200 transition-transform duration-300 ease-in-out`}
        >
          <nav className="mt-8">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;

              return (
                <div key={item.id} className="relative">
                  <button
                    onClick={() => {
                      if (item.id === 'documents') {
                        navigate('/documents');
                      } else {
                        setActiveSection(item.id);
                      }
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-6 py-3 text-left transition-colors duration-200 group ${
                      isActive
                        ? "bg-teal-50 text-teal-600 border-r-2 border-teal-500"
                        : "text-gray-700 hover:bg-gray-50 hover:text-teal-600"
                    }`}
                  >
                    <div className="flex items-center">
                      <Icon className="h-5 w-5 mr-3" />
                      <div className="text-left">
                        <div className="font-medium">{item.label}</div>
                        {item.description && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {item.description}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      {item.count !== null && item.count > 0 && (
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          item.priority === 'urgent' 
                            ? 'bg-red-100 text-red-800 animate-pulse'
                            : 'bg-teal-100 text-teal-800'
                        }`}>
                          {item.count}
                        </span>
                      )}
                      {item.priority === 'urgent' && (
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
          </nav>

          {/* Sidebar Footer intentionally left empty */}
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 pb-20">{renderContent()}</main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default FinancialDashboard;

// Reusable lifecycle chips renderer (same mapping used across dashboards)
function renderLifecycleChips(application) {
  const w = application?.workflow || {};
  const mv = w.medicalVerification || {};
  const mr = w.medicalReview || {};
  const ds = w.dischargeSettlement || {};
  const disb = w.disbursal || {};
  const dispatch = w.dispatch || w.dispatches || {};
  const enach = w.enach || {};

  const chips = [];

  const isInProgress = mr?.status === 'in_progress' || application?.status === 'under_medical_review' || application?.status === 'submitted' || application?.status === 'medical_review_pending';
  if (isInProgress) chips.push({ label: 'Processing', cls: 'bg-blue-100 text-blue-800 border-blue-200' });

  if (mv?.kypGenerated) chips.push({ label: 'KYP Generated', cls: 'bg-green-100 text-green-800 border-green-200' });

  const kypAck = mv?.kypHospitalAcknowledgement?.status;
  if (kypAck === 'accepted') chips.push({ label: 'KYP Accepted', cls: 'bg-green-100 text-green-800 border-green-200' });
  else if (kypAck === 'challenged') chips.push({ label: 'KYP Challenged', cls: 'bg-yellow-100 text-yellow-800 border-yellow-200' });
  else if (kypAck === 'rejected') chips.push({ label: 'KYP Rejected', cls: 'bg-red-100 text-red-800 border-red-200' });

  const dischargeStarted = application?.dischargeInitiated || ds?.status;
  if (dischargeStarted) chips.push({ label: 'Discharge Started', cls: 'bg-purple-100 text-purple-800 border-purple-200' });

  if (ds?.finalApprovalLetterUrl) chips.push({ label: 'FAL Generated', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' });

  const falAck = ds?.hospitalAcknowledgement?.status;
  const sanctions = w?.sanctions;
  const anyFalChallenged = Array.isArray(sanctions) && sanctions.some(s => s?.hospitalAcknowledgement?.status === 'challenged');
  const anyFalRejected = Array.isArray(sanctions) && sanctions.some(s => s?.hospitalAcknowledgement?.status === 'rejected');
  const allFalAccepted = Array.isArray(sanctions) && sanctions.length > 0 && sanctions.every(s => s?.hospitalAcknowledgement?.status === 'accepted');
  if (falAck === 'accepted' || allFalAccepted) chips.push({ label: 'FAL Accepted', cls: 'bg-green-100 text-green-800 border-green-200' });
  else if (falAck === 'challenged' || anyFalChallenged) chips.push({ label: 'FAL Challenged', cls: 'bg-yellow-100 text-yellow-800 border-yellow-200' });
  else if (falAck === 'rejected' || anyFalRejected) chips.push({ label: 'FAL Rejected', cls: 'bg-red-100 text-red-800 border-red-200' });

  if (dispatch?.status) chips.push({ label: `Dispatch: ${String(dispatch.status).replace(/_/g,' ')}`, cls: 'bg-sky-100 text-sky-800 border-sky-200' });
  else if (ds?.finalApprovalLetterUrl && !disb?.status) chips.push({ label: 'Dispatch Pending', cls: 'bg-sky-50 text-sky-700 border-sky-200' });

  if (disb?.status === 'disbursed') chips.push({ label: 'Disbursed', cls: 'bg-teal-100 text-teal-800 border-teal-200' });
  else if (disb?.status) chips.push({ label: `Disbursal: ${String(disb.status).replace(/_/g,' ')}`, cls: 'bg-teal-50 text-teal-800 border-teal-200' });

  if (enach?.status === 'initiated') chips.push({ label: 'eNach Initiated', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' });
  else if (enach?.status === 'completed') chips.push({ label: 'eNach Done', cls: 'bg-indigo-100 text-indigo-800 border-indigo-300' });

  if (chips.length === 0) chips.push({ label: 'Processing', cls: 'bg-blue-50 text-blue-700 border-blue-200' });

  return (
    <div className="flex flex-wrap items-center gap-1">
      {chips.map((c, idx) => (
        <span key={idx} className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${c.cls}`}>
          {c.label}
        </span>
      ))}
    </div>
  );
}